// Вкладка «Обзор»: кто это, где работает и в каком он состоянии.
//
// ⚠️ Ключевая особенность, ради которой этот файл написан именно так: **скрытое поле
// ОТСУТСТВУЕТ в ответе**. У табельщика в карточке нет ключа `birth_date` вовсе, а не пустое
// значение (server/app/serializers/employees.py, сценарий 10). Разница смысловая: пустое
// значение читается как «не заполнено, надо дозаполнить», а отсутствие ключа означает «не
// ваше». Поэтому строка даты рождения рисуется ТОЛЬКО когда ключ пришёл — и не рисуется
// пустой строкой с прочерком, из-за которой табельщик пошёл бы искать паспорт.

import { RiBuilding4Line } from '@remixicon/react';
import { Link } from 'react-router';

import type { EmployeeOut } from '@/shared/api/endpoints/people';
import { hasField } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { useПереносДолгов, состояниеПереноса } from '@/shared/debts/перенос';
import { can } from '@/shared/rbac/permissions';
import { formatDateOnly } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import { formatPhone } from '@/shared/text/phone';
import { FieldList, FieldRow } from '@/shared/ui/field-row';

import { DuplicateNotice } from '../components/DuplicateNotice';
import { FinalSettlementNotice } from './FinalSettlementNotice';
import { STATUS_TITLES } from '../dictionaries';
import { useDuplicateWarning } from '../queries';
import type { PeopleRoute } from '../route';
import { FILTER_SITE, peopleHref, setFilter } from '../route';

export function OverviewTab({ employee, route }: { employee: EmployeeOut; route: PeopleRoute }) {
  // Совпадения от заведения карточки: сервер отдаёт их один раз, вместе с созданием.
  const duplicateWarning = useDuplicateWarning(employee.id);
  // «Долга нет, всё закрыто» и «компенсация не использована» — утверждения о прошлом, которого
  // программа не видела: до начала работы в ней ничего сюда не переносилось. Пока переноса не
  // было, обе строки говорят про программу, а не про человека (23.09.2026).
  const видитДолги = can(
    useAuthStore((state) => state.permissions),
    'core.debt.view',
  );
  const перенос = состояниеПереноса(useПереносДолгов(видитДолги));
  const прошлоеИзвестно = перенос === 'перенесены';

  return (
    <div className='flex flex-col gap-4 p-5'>
      {/* Блокировка расчёта стоит первой: это единственное, что требует действия прямо
          сейчас. Ниже — справочные поля, которые подождут. */}
      <FinalSettlementNotice employeeId={employee.id} />

      {duplicateWarning !== undefined && (
        <DuplicateNotice
          report={duplicateWarning}
          note='Карточка уже создана — это предупреждение, а не отказ. Если это тот же человек, перенесите данные в найденную карточку и уволите лишнюю; если однофамилец, ничего делать не нужно.'
        />
      )}

      {/* Имя и состояние занятости стоят в ШАПКЕ карточки и здесь не повторяются: человек
          читает одно и то же дважды и решает, что это разные поля. */}
      <FieldList title='Кто это'>
        {/* Ключа нет — строки нет: см. заголовок файла. */}
        {hasField(employee, 'birth_date') && (
          <FieldRow
            label='Дата рождения'
            value={
              employee.birth_date === null || employee.birth_date === undefined
                ? null
                : formatDateOnly(employee.birth_date)
            }
            // Следствия нет намеренно: кто вносит дату рождения — это порядок работы, а
            // не факт об этом человеке, и на карточке ему делать нечего.
            missing='не заполнена'
          />
        )}
        <FieldRow
          label='Телефон'
          value={formatPhone(employee.phone_normalized)}
          missing='не указан'
          missingNote='связаться с человеком можно только через объект'
        />
        {employee.phone_extra !== null && (
          <FieldRow label='Дополнительный телефон' value={formatPhone(employee.phone_extra)} />
        )}
        {/* «не указан», а не «город не указан»: подпись строки уже сказала, какой город. */}
        <FieldRow label='Город' value={employee.city} missing='не указан' />
        <FieldRow
          label='Табельный номер'
          value={employee.personnel_no}
          missing='не присвоен'
          missingNote='в ведомость человек попадёт по фамилии'
        />
      </FieldList>

      <FieldList title='Как оформлен и где работает'>
        {/* Состояние занятости повторяется здесь ТОЛЬКО у уволенного: там оно несёт дату и
            пояснение про сохранённые долги, которых в метке шапки нет. */}
        {employee.status === 'fired' && (
          <FieldRow
            label='Статус занятости'
            value={STATUS_TITLES[employee.status]}
            // Даты может не быть: «уволен» ставят и пачкой из реестра, а там её не спрашивают.
            // «Увольнение —» с прочерком на месте даты читалось как поломка, поэтому без даты
            // остаётся только вторая половина фразы.
            note={
              employee.fired_at == null || employee.fired_at === ''
                ? 'история и долги сохранены'
                : `увольнение ${formatDateOnly(employee.fired_at)}; история и долги сохранены`
            }
          />
        )}
        <FieldRow
          label='Тип оформления'
          value={employee.employment_type_title}
          missing='не проставлен'
          missingNote='человек не попадёт ни в одно представление по оформлению'
        />
        <FieldRow
          label='Дата приёма'
          value={employee.hired_at === null ? null : formatDateOnly(employee.hired_at)}
          missing='не заполнена'
          missingNote='стаж и первая вахта считаться не будут'
        />
        <FieldRow
          label='Объект'
          value={
            employee.site_id === null || employee.site_name === null ? null : (
              <Link
                to={peopleHref(
                  route.view,
                  setFilter({ filters: {}, panels: [] }, FILTER_SITE, employee.site_id),
                )}
                className='inline-flex items-center gap-1.5 text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'
              >
                <RiBuilding4Line className='size-4' />
                {employee.site_name}
              </Link>
            )
          }
          missing='назначения на сегодня нет'
          missingNote='человек числится, но не стоит ни на одном посту'
          note={employee.region === null ? undefined : `регион объекта: ${employee.region}`}
        />
        <FieldRow
          label='Разряд'
          value={employee.guard_category === null ? null : `${employee.guard_category} разряд`}
          missing='нет действующего удостоверения'
          missingNote='на посты с требованием разряда не поставить'
        />
      </FieldList>

      <FieldList title='Деньги и обязательства'>
        <FieldRow
          label='Непогашенный долг'
          value={
            employee.has_debt ? (
              <Badge.Root variant='lighter' color='red' size='medium'>
                есть
              </Badge.Root>
            ) : прошлоеИзвестно ? (
              'нет'
            ) : (
              'в программе нет'
            )
          }
          note={
            employee.has_debt
              ? 'увольнение с непогашенным долгом требует решения руководства — долг не исчезает вместе с человеком'
              : прошлоеИзвестно
                ? 'на человеке нет активных долгов: билеты, обучение, форма и удостоверение закрыты'
                : 'долги до начала работы в программе сюда ещё не перенесены'
          }
        />
        <FieldRow
          label='Компенсация билета'
          value={
            employee.ticket_compensation_used
              ? 'уже использована'
              : прошлоеИзвестно
                ? 'не использована'
                : 'в программе не применялась'
          }
          note={
            employee.ticket_compensation_used || прошлоеИзвестно
              ? 'компенсация билета положена один раз'
              : 'выплачивали ли её до начала работы в программе, сюда не переносилось, а положена она один раз'
          }
        />
      </FieldList>
    </div>
  );
}
